import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './ProfileMenu.css';

type ThemeMode = 'light' | 'dark' | 'system';

interface ProfileMenuProps {
    themeMode: ThemeMode;
    onThemeToggle: () => void;
    onLogout: () => void;
    onShowSystemInfo?: () => void;
    onNavigate?: (path: string) => void;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
    // themeMode,
    // onThemeToggle,
    onLogout,
    onShowSystemInfo,
    onNavigate
}) => {
    const { t } = useTranslation();
    const { user, isAuthenticated } = useAuth();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogoutClick = () => {
        onLogout();
        setIsMenuOpen(false);
    };

    const getRoleBadgeClass = (role: string) => {
        switch (role) {
            case 'ADMIN':
                return 'role-badge admin';
            case 'USER':
                return 'role-badge user';
            case 'FREE':
                return 'role-badge free';
            default:
                return 'role-badge';
        }
    };

    // Only show profile menu for authenticated users
    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="profile-menu" ref={menuRef}>
            <button 
                type="button"
                className="profile-button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                title={t('profile.menu')}
            >
                <span className="material-icons">account_circle</span>
                <span className="material-icons">
                    {isMenuOpen ? 'expand_less' : 'expand_more'}
                </span>
            </button>

            {isMenuOpen && (
                <div className="profile-dropdown">
                    {/* User Info Section */}
                    <div className="profile-section">
                        <div className="profile-user-info">
                            <div className="profile-username-display">
                                <span className="material-icons">account_circle</span>
                                <div className="user-details">
                                    <div className="username">{user?.username}</div>
                                </div>
                            </div>
                            <div className="profile-role">
                                <span className="material-icons">
                                    {user?.role === 'ADMIN' ? 'admin_panel_settings' : 
                                     user?.role === 'USER' ? 'star' : 
                                     user?.role === 'FREE' ? 'person' : 'help'}
                                </span>
                                <span className={getRoleBadgeClass(user?.role || '')}>
                                    {user?.role === 'USER' ? 'PRO' : user?.role}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="profile-divider"></div>

                    {/* About Section */}
                    <div className="profile-section">
                        <button 
                            type="button"
                            className="profile-menu-item"
                            onClick={() => {
                                onShowSystemInfo?.();
                                setIsMenuOpen(false);
                            }}
                        >
                            <span className="material-icons">info</span>
                            <span>{t('profile.about')}</span>
                            <span className="material-icons action-icon">chevron_right</span>
                        </button>
                    </div>

                    {/* Benefits Section - Only for Admins */}
                    {user?.role === 'ADMIN' && (
                        <>
                            <div className="profile-divider"></div>
                            <div className="profile-section">
                                <button 
                                    type="button"
                                    className="profile-menu-item"
                                    onClick={() => {
                                        onNavigate?.('/benefits');
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    <span className="material-icons">card_giftcard</span>
                                    <span>{t('navigation.benefits')}</span>
                                    <span className="material-icons action-icon">chevron_right</span>
                                </button>
                            </div>
                        </>
                    )}

                    <div className="profile-divider"></div>

                    {/* Account Actions Section */}
                    <div className="profile-section">
                        <button 
                            type="button"
                            className="profile-menu-item"
                            onClick={() => {
                                onNavigate?.('/settings');
                                setIsMenuOpen(false);
                            }}
                        >
                            <span className="material-icons">settings</span>
                            <span>{t('profile.accountSettings')}</span>
                            <span className="material-icons action-icon">chevron_right</span>
                        </button>

                        <button 
                            type="button"
                            className="profile-menu-item"
                            onClick={() => {
                                onNavigate?.('/feedback');
                                setIsMenuOpen(false);
                            }}
                        >
                            <span className="material-icons">feedback</span>
                            <span>Feedback</span>
                            <span className="material-icons action-icon">chevron_right</span>
                        </button>

                        {user?.role === 'FREE' && (
                            <button 
                                type="button"
                                className="profile-menu-item upgrade-item"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                <span className="material-icons">star</span>
                                <span>{t('profile.upgradeToPro')}</span>
                                <span className="material-icons action-icon">chevron_right</span>
                            </button>
                        )}

                        <button 
                            type="button"
                            className="profile-menu-item logout-item"
                            onClick={handleLogoutClick}
                        >
                            <span className="material-icons">logout</span>
                            <span>{t('auth.logout')}</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileMenu; 